import { useEffect, useState } from "react";
import { Header } from "./Header";

type Lead = {
  id: string;
  token: string;
  nombre: string;
  empresa: string;
  cargo: string;
  interes: string;
  telefono_e164: string | null;
  email: string | null;
  utm_source: string | null;
  created_at: string;
  contactado_at: string | null;
};

function formatCreatedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-CL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toCsv(leads: Lead[]): string {
  const header = [
    "created_at",
    "token",
    "nombre",
    "empresa",
    "cargo",
    "interes",
    "telefono_e164",
    "email",
    "utm_source",
    "contactado_at",
  ];
  const rows = leads.map((l) =>
    header
      .map((h) => {
        const v = l[h as keyof Lead];
        if (v === null || v === undefined) return "";
        const s = String(v).replace(/"/g, '""');
        return `"${s}"`;
      })
      .join(",")
  );
  return [header.join(","), ...rows].join("\n");
}

export function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function doLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch("/api/v1/comercial/admin/leads", {
        headers: {
          Authorization: "Basic " + btoa("admin:" + password),
        },
      });
      if (resp.status === 401) {
        setError("Contraseña incorrecta.");
        setAuthed(false);
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as { leads: Lead[] };
      setLeads(data.leads);
      setAuthed(true);
      sessionStorage.setItem("rk_admin_pw", password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error cargando leads.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const stored = sessionStorage.getItem("rk_admin_pw");
    if (stored) {
      setPassword(stored);
      void (async () => {
        setLoading(true);
        try {
          const resp = await fetch("/api/v1/comercial/admin/leads", {
            headers: { Authorization: "Basic " + btoa("admin:" + stored) },
          });
          if (resp.ok) {
            const data = (await resp.json()) as { leads: Lead[] };
            setLeads(data.leads);
            setAuthed(true);
          } else {
            sessionStorage.removeItem("rk_admin_pw");
          }
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  function downloadCsv() {
    const csv = toCsv(leads);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `risk594_leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function logout() {
    sessionStorage.removeItem("rk_admin_pw");
    setAuthed(false);
    setPassword("");
    setLeads([]);
  }

  if (!authed) {
    return (
      <div className="container">
        <Header />
        <div className="admin-login">
          <h2>Admin · Leads Risk594</h2>
          {error && <div className="err">{error}</div>}
          <form onSubmit={doLogin}>
            <div className="field">
              <label htmlFor="pw">Contraseña</label>
              <input
                id="pw"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="submit" disabled={loading}>
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide">
      <Header />
      <div className="admin-toolbar">
        <h2>Leads ({leads.length})</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="csv" onClick={downloadCsv}>
            Descargar CSV
          </button>
          <button className="csv" onClick={logout} style={{ background: "#607684" }}>
            Salir
          </button>
        </div>
      </div>
      {leads.length === 0 ? (
        <p className="lead">Todavía no hay leads capturados.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Token</th>
              <th>Nombre</th>
              <th>Empresa</th>
              <th>Cargo</th>
              <th>Interés</th>
              <th>Contacto</th>
              <th>UTM</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id}>
                <td>{formatCreatedAt(l.created_at)}</td>
                <td className="token-cell">{l.token}</td>
                <td>{l.nombre}</td>
                <td>{l.empresa}</td>
                <td>{l.cargo}</td>
                <td title={l.interes}>
                  {l.interes.length > 60 ? l.interes.slice(0, 60) + "…" : l.interes}
                </td>
                <td>
                  {l.telefono_e164 ? <div>📱 {l.telefono_e164}</div> : null}
                  {l.email ? <div>✉ {l.email}</div> : null}
                </td>
                <td>{l.utm_source ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
